import {
  Component,
  EventEmitter,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  ValidatorFn,
  Validators
} from '@angular/forms';

import { I18NextModule } from 'angular-i18next';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatRadioModule } from '@angular/material/radio';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { RouterModule } from '@angular/router';
import { MatStepperModule } from '@angular/material/stepper';
import { MatSelectModule } from '@angular/material/select';
import { MatButtonModule } from '@angular/material/button';
import { ProjectType } from 'src/app/shared/model/projectType.enum';
import { OrderService } from 'src/app/core/services/order.service';
import {
  Areas,
  Dealers,
  Departaments
} from 'src/app/shared/model/project.model';
import { FeedbackModalComponent } from 'src/app/shared/components/feedback-modal/feedback-modal.component';
import { MatDialog } from '@angular/material/dialog';
import i18next from 'i18next';
import {
  MatDatepicker,
  MatDatepickerModule
} from '@angular/material/datepicker';
import { DateAdapter, NativeDateAdapter } from '@angular/material/core';
import { VehiclesService } from 'src/app/core/services/vehicles.service';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
export class CustomDateAdapter extends NativeDateAdapter {
  override getFirstDayOfWeek(): number {
    return 1;
  }

  //format dd/mm/yyyy
  override format(date: Date): string {
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  }

  //parse dd/mm/yyyy
  override parse(value: string): Date | null {
    if (typeof value === 'string') {
      const parts = value.split('/');
      const day = Number(parts[0]);
      const month = Number(parts[1]) - 1;
      const year = Number(parts[2]);
      return new Date(year, month, day);
    }
    return null;
  }
}
@Component({
  standalone: true,
  selector: 'app-step-projects',
  templateUrl: './step-projects.component.html',
  styleUrls: ['./step-projects.component.scss'],
  imports: [
    CommonModule,
    RouterModule,
    I18NextModule,
    FormsModule,
    ReactiveFormsModule,
    MatCheckboxModule,
    MatRadioModule,
    MatSelectModule,
    MatStepperModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatDatepickerModule,
    MatAutocompleteModule
  ],
  providers: [{ provide: DateAdapter, useClass: CustomDateAdapter }]
})
export class StepProjectsComponent implements OnInit {
  form!: FormGroup;
  projectTypeEnum = ProjectType;
  departaments: Departaments[] = [];
  areas: Areas[] = [];
  dealers: Dealers[] = [];
  cecos: string[] = [];
  showAreaAndDepartament = false;
  showReguForm = false;
  showOtherForm = false;
  showNewForm = false;
  selectedSubtype = '';
  selectedSpecialType = '';
  minDate = new Date();
  searchForm: FormGroup;
  searchError = false;
  vinRepeat = false;
  vehicles: any[] = [];
  stockVehicle = false;

  @ViewChild('picker') picker!: MatDatepicker<any>;
  @ViewChild('datePicker') datePicker!: MatDatepicker<any>;
  @ViewChild('plateNumberPicker') plateNumberPicker!: MatDatepicker<any>;
  @ViewChild('deliveryPicker') deliveryPicker!: MatDatepicker<any>;
  @ViewChild('sopPicker') sopPicker!: MatDatepicker<any>;

  @Output() formChange = new EventEmitter<any>();
  filteredDealers: Dealers[] = [];

  constructor(
    private orderService: OrderService,
    private fb: FormBuilder,
    public dialog: MatDialog,
    private vehicleService: VehiclesService
  ) {
    this.searchForm = this.fb.group({
      vin: [''],
      plateNumber: ['']
    });
  }

  ngOnInit(): void {
    this.form = this.fb.group({
      subtype: ['', Validators.required]
    });

    this.orderService.getProjectAreas().subscribe({
      next: (data: any) => {
        this.areas = data;
      },
      error: (error) => {
        console.error('Error fetching authorizers:', error);
      },
      complete: () => {
        console.log('Fetch complete');
      }
    });

    this.form.valueChanges.subscribe((value) => {
      this.formChange.emit({ ...value, invalid: this.form.invalid });
    });

    this.orderService.getProjectDealers().subscribe({
      next: (data: any) => {
        this.dealers = data;
        this.filteredDealers = data;
      },
      error: (error) => {
        console.error('Error fetching authorizers:', error);
        this.dealers = [];
        this.filteredDealers = [];
      },
      complete: () => {
        console.log('Fetch complete');
      }
    });
  }

  displayDealerFn(dealer: Dealers): string {
    return dealer && dealer.code ? `${dealer.code} - ${dealer.name}` : '';
  }
  dealerObjectValidator(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      const value = control.value;
      if (!value) {
        return null;
      }
      if (typeof value === 'string' && value.length > 0) {
        return { invalidDealer: true };
      }
      return null;
    };
  }

  onSubtypeChange(): void {
    this.selectedSubtype = this.form.value.subtype;
    this.stockVehicle = false;
    this.updateForm(this.selectedSubtype);
    this.vehicles = [];

    this.showAreaAndDepartament =
      this.selectedSubtype === this.projectTypeEnum.NEW ||
      this.selectedSubtype === this.projectTypeEnum.REGU;

    this.showReguForm = this.selectedSubtype === this.projectTypeEnum.REGU;
    this.showOtherForm = this.selectedSubtype === this.projectTypeEnum.OTHER;
    this.showNewForm = this.selectedSubtype === this.projectTypeEnum.NEW;

    if (this.showReguForm) {
      this.stockVehicle = true;
    }

    this.form.get('area')?.setValue('');
    this.form.get('departament')?.setValue('');
    const dealerCtrl = this.form.get('dealerCode');
    const cecoCtrl = this.form.get('ceco');

    if (dealerCtrl) {
      dealerCtrl.setValue('', { emitEvent: true });
      dealerCtrl.markAsPristine();
      dealerCtrl.markAsUntouched();
      dealerCtrl.setErrors(null);
    }

    if (cecoCtrl) {
      cecoCtrl.setValue('', { emitEvent: false });
      cecoCtrl.disable({ emitEvent: false });
      cecoCtrl.markAsPristine();
      cecoCtrl.markAsUntouched();
      cecoCtrl.setErrors(null);
    }

    this.cecos = [];
    this.filteredDealers = [...this.dealers];

    this.vehicles = [];

    const formValue = this.form.getRawValue();
    this.formChange.emit({ ...formValue, invalid: this.form.invalid });
  }

  onSpecialTypeChange(): void {
    this.selectedSpecialType = this.form.value.specialType;
    this.form.get('projectType')?.setValue('EMPTY');
    this.form.get('projectType')?.enable();
    this.form.get('projectML')?.enable();
    this.form.get('cops')?.clearValidators();
    this.form.get('cops')?.updateValueAndValidity();

    if (
      this.selectedSpecialType !== 'PROTOTYPE' &&
      this.selectedSpecialType !== 'SERIE'
    ) {
      this.form.get('preseriesCost')?.setValue(null);
      this.form.get('seriesCost')?.setValue(null);
      this.form.get('preseriesCost')?.setValidators(Validators.required);
      this.form.get('seriesCost')?.setValidators(Validators.required);
      this.form.get('preseriesCost')?.updateValueAndValidity();
      this.form.get('seriesCost')?.updateValueAndValidity();
    }
    if (
      this.selectedSpecialType === 'PROTOTYPE' ||
      this.selectedSpecialType === 'SERIE'
    ) {
      this.form.get('preseriesType')?.setValue('EMPTY');
      this.form.get('preseriesType')?.disable({ emitEvent: false });
      this.form.get('projectType')?.setValue('EMPTY');
      this.form.get('projectType')?.disable({ emitEvent: false });
      this.form.get('projectML')?.disable({ emitEvent: false });
      this.form.get('preseriesCost')?.setValue(0);
      this.form.get('seriesCost')?.setValue(0);
      this.form.get('preseriesCost')?.clearValidators();
      this.form.get('seriesCost')?.clearValidators();
      this.form.get('preseriesCost')?.updateValueAndValidity();
      this.form.get('seriesCost')?.updateValueAndValidity();
    } else if (
      this.selectedSpecialType === 'PRESERIE' &&
      this.form.value.projectType === 'EMPTY'
    ) {
      this.form.get('preseriesType')?.setValue('PVS');
      this.form.get('preseriesType')?.disable({ emitEvent: false });
      this.form.get('preseriesCost')?.setValue(0);
      this.form.get('seriesCost')?.setValue(0);
      this.form.get('preseriesCost')?.clearValidators();
      this.form.get('seriesCost')?.clearValidators();
      this.form.get('preseriesCost')?.updateValueAndValidity();
      this.form.get('seriesCost')?.updateValueAndValidity();
      this.form.get('cops')?.setValidators(Validators.required);
      this.form.get('cops')?.updateValueAndValidity();
    } else if (
      this.selectedSpecialType === 'PRESERIE' &&
      this.form.value.projectType !== 'EMPTY'
    ) {
      this.form.get('preseriesType')?.setValue('PVS');
      this.form.get('preseriesType')?.disable({ emitEvent: false });
      this.form.get('preseriesCost')?.setValue(null);
      this.form.get('seriesCost')?.setValue(null);
      this.form.get('preseriesCost')?.setValidators(Validators.required);
      this.form.get('seriesCost')?.setValidators(Validators.required);
      this.form.get('preseriesCost')?.updateValueAndValidity();
      this.form.get('seriesCost')?.updateValueAndValidity();
      this.form.get('cops')?.setValidators(Validators.required);
      this.form.get('cops')?.updateValueAndValidity();
    } else if (this.selectedSpecialType === 'PRE_PRESERIE') {
      this.form.get('preseriesType')?.setValue('VFF');
      this.form.get('preseriesCost')?.setValue(0);
      this.form.get('seriesCost')?.setValue(0);
      this.form.get('preseriesCost')?.clearValidators();
      this.form.get('seriesCost')?.clearValidators();
      this.form.get('preseriesType')?.disable({ emitEvent: false });
      this.form.get('cops')?.setValidators(Validators.required);
      this.form.get('cops')?.updateValueAndValidity();
    } else if (this.selectedSpecialType === 'ZEROSERIE') {
      this.form.get('preseriesType')?.setValue('ZEROSERIE');
      this.form.get('preseriesCost')?.setValue(0);
      this.form.get('seriesCost')?.setValue(0);
      this.form.get('preseriesCost')?.clearValidators();
      this.form.get('seriesCost')?.clearValidators();
      this.form.get('preseriesType')?.disable({ emitEvent: false });
      this.form.get('cops')?.setValidators(Validators.required);
      this.form.get('cops')?.updateValueAndValidity();
    } else if (this.selectedSpecialType === 'PRESS') {
      this.form.get('preseriesType')?.setValue('EMPTY');
      this.form.get('preseriesType')?.enable();
      this.form.get('pressSubtype')?.setValidators(Validators.required);
      this.form.get('pressSubtype')?.updateValueAndValidity();
      this.form.get('projectType')?.setValue('EMPTY');
      this.form.get('projectType')?.disable({ emitEvent: false });
      this.form.get('projectML')?.disable({ emitEvent: false });
    }

    if (this.form.get('cops')?.invalid) {
      const dialogRef = this.dialog.open(FeedbackModalComponent, {
        width: '400px',
        data: {
          text: i18next.t('copsRequired'),
          close: true,
          theme: 'light'
        }
      });
      dialogRef.afterClosed().subscribe(() => {
        setTimeout(() => {
          const element = document.getElementById('cops-anchor');
          if (element) {
            element.scrollIntoView({ behavior: 'smooth' });
          }
        }, 0);
      });
    }
  }

  onPreseriesTypeChange(): void {
    const selectedPreseriesType = this.form.value.preseriesType;
    this.form.get('projectType')?.setValue('EMPTY');
    this.form.get('projectType')?.enable();
    this.form.get('projectML')?.enable();

    if (selectedPreseriesType === 'EMPTY') {
      this.form.get('projectType')?.setValue('EMPTY');
      this.form.get('projectType')?.disable({ emitEvent: false });
      this.form.get('projectML')?.disable({ emitEvent: false });
    }
  }

  onProjectTypeChange(): void {
    this.selectedSpecialType = this.form.value.specialType;
    if (
      this.selectedSpecialType === 'PRESERIE' &&
      this.form.value.projectType === 'EMPTY'
    ) {
      this.form.get('preseriesType')?.setValue('PVS');
      this.form.get('preseriesType')?.disable({ emitEvent: false });
      this.form.get('preseriesCost')?.setValue(0);
      this.form.get('seriesCost')?.setValue(0);
      this.form.get('preseriesCost')?.clearValidators();
      this.form.get('seriesCost')?.clearValidators();
      this.form.get('preseriesCost')?.updateValueAndValidity();
      this.form.get('seriesCost')?.updateValueAndValidity();
    } else if (
      this.selectedSpecialType === 'PRESERIE' &&
      this.form.value.projectType !== 'EMPTY'
    ) {
      this.form.get('preseriesType')?.setValue('PVS');
      this.form.get('preseriesType')?.disable({ emitEvent: false });
      this.form.get('preseriesCost')?.setValue(null);
      this.form.get('seriesCost')?.setValue(null);
      this.form.get('preseriesCost')?.setValidators(Validators.required);
      this.form.get('seriesCost')?.setValidators(Validators.required);
      this.form.get('preseriesCost')?.updateValueAndValidity();
      this.form.get('seriesCost')?.updateValueAndValidity();
    }
  }

  updateForm(selectedSubtype: string): void {
    Object.keys(this.form.controls).forEach((key) => {
      if (key !== 'subtype') {
        this.form.removeControl(key);
      }
    });
    this.form.reset({
      subtype: selectedSubtype
    });
    switch (selectedSubtype) {
      case this.projectTypeEnum.NEW:
        this.form.addControl('area', this.fb.control('', Validators.required));
        this.form.addControl(
          'departament',
          this.fb.control({ value: '', disabled: true }, Validators.required)
        );
        this.form.addControl('reference', this.fb.control(''));
        this.form.addControl(
          'date',
          this.fb.control('', [Validators.required, this.minDateToday()])
        );
        this.form.addControl(
          'returnDate',
          this.fb.control('', Validators.required)
        );
        this.form.addControl('sop', this.fb.control(''));
        this.form.addControl('kms', this.fb.control(null));
        this.form.addControl('bnr', this.fb.control(''));
        this.form.addControl(
          'finish',
          this.fb.control('', Validators.required)
        );
        this.form.addControl('country', this.fb.control(''));
        this.form.addControl('model', this.fb.control('', Validators.required));
        this.form.addControl('my', this.fb.control(''));
        this.form.addControl(
          'tmaimg',
          this.fb.control('', Validators.required)
        );
        this.form.addControl(
          'launch',
          this.fb.control({ value: true, disabled: true })
        );
        this.form.addControl(
          'dealerCode',
          this.fb.control('', [
            Validators.required,
            this.dealerObjectValidator()
          ])
        );
        this.form.addControl('ceco', this.fb.control('', Validators.required));
        this.form.addControl('use', this.fb.control(''));
        this.form.addControl('exteriorColor', this.fb.control(''));
        this.form.addControl('interiorColor', this.fb.control(''));
        this.form.addControl('destination', this.fb.control(''));
        this.form.addControl('optionals', this.fb.control(''));
        this.form.addControl('prNumbers', this.fb.control(''));
        this.form.addControl('prMotor', this.fb.control(''));
        this.form.addControl('descriptionEngine', this.fb.control(''));
        this.form.addControl('ea', this.fb.control(''));
        this.form.addControl('cops', this.fb.control(''));
        this.form.addControl('vds', this.fb.control(''));
        this.form.addControl('registered', this.fb.control(false));
        this.form.addControl(
          'specialType',
          this.fb.control('', Validators.required)
        );
        this.form.addControl('pressSubtype', this.fb.control(''));
        this.form.addControl('subtypeSeries', this.fb.control(''));
        this.form.addControl(
          'materialType',
          this.fb.control('', Validators.required)
        );
        this.form.addControl(
          'preseriesType',
          this.fb.control('', Validators.required)
        );
        this.form.addControl('projectType', this.fb.control('EMPTY'));
        this.form.addControl('projectML', this.fb.control(''));
        this.form.addControl('preseriesCost', this.fb.control(null));
        this.form.addControl('seriesCost', this.fb.control(null));
        this.form.addControl('transferToThird', this.fb.control(false));
        break;
      case this.projectTypeEnum.REGU:
        this.form.addControl('area', this.fb.control('', Validators.required));
        this.form.addControl(
          'departament',
          this.fb.control({ value: '', disabled: true }, Validators.required)
        );
        this.form.addControl('reference', this.fb.control(''));
        this.form.addControl('date', this.fb.control('', Validators.required));
        this.form.addControl(
          'returnDate',
          this.fb.control('', Validators.required)
        );
        this.form.addControl('vin', this.fb.control(''));
        this.form.addControl('sop', this.fb.control(''));
        this.form.addControl('kms', this.fb.control(null));
        this.form.addControl('bnr', this.fb.control(''));
        this.form.addControl(
          'finish',
          this.fb.control('', Validators.required)
        );
        this.form.addControl('country', this.fb.control(''));
        this.form.addControl('model', this.fb.control('', Validators.required));
        this.form.addControl('my', this.fb.control(''));
        this.form.addControl(
          'tmaimg',
          this.fb.control('', Validators.required)
        );
        this.form.addControl(
          'launch',
          this.fb.control({ value: false, disabled: true })
        );
        this.form.addControl(
          'dealerCode',
          this.fb.control('', [
            Validators.required,
            this.dealerObjectValidator()
          ])
        );
        this.form.addControl('ceco', this.fb.control('', Validators.required));
        this.form.addControl('use', this.fb.control(''));
        this.form.addControl('color', this.fb.control('', Validators.required));
        this.form.addControl('destination', this.fb.control(''));
        this.form.addControl('optionals', this.fb.control(''));
        this.form.addControl('prNumbers', this.fb.control(''));
        this.form.addControl('prMotor', this.fb.control(''));
        this.form.addControl('descriptionEngine', this.fb.control(''));
        this.form.addControl('orderNumber', this.fb.control(''));
        this.form.addControl('ea', this.fb.control(''));
        this.form.addControl('cops', this.fb.control(''));
        this.form.addControl('vds', this.fb.control(''));
        this.form.addControl('registered', this.fb.control(false));
        this.form.addControl('stockVehicle', this.fb.control(false));
        break;
      case this.projectTypeEnum.OTHER:
        this.form.addControl('checkedManagement', this.fb.control(false));
        this.form.addControl('plateNumber', this.fb.control(''));
        this.form.addControl('plateNumberDate', this.fb.control(''));
        this.form.addControl('townHallCode', this.fb.control(''));
        this.form.addControl('insuranceType', this.fb.control(''));
        this.form.addControl('deliveryDate', this.fb.control(''));
        this.form.addControl('brand', this.fb.control('', Validators.required));
        this.form.addControl('reference', this.fb.control(''));
        this.form.addControl(
          'launch',
          this.fb.control({ value: false, disabled: true })
        );
        this.form.addControl(
          'dealerCode',
          this.fb.control('', [
            Validators.required,
            this.dealerObjectValidator()
          ])
        );
        this.form.addControl('ceco', this.fb.control('', Validators.required));
        this.form.addControl('date', this.fb.control('', Validators.required));
        this.form.addControl('usePreview', this.fb.control(''));
        this.form.addControl(
          'returnDate',
          this.fb.control('', Validators.required)
        );
        this.form.addControl('kms', this.fb.control(''));
        this.form.addControl('finishes', this.fb.control(''));
        this.form.addControl('model', this.fb.control('', Validators.required));
        this.form.addControl('engine', this.fb.control(''));
        this.form.addControl('color', this.fb.control(''));
        this.form.addControl('cops', this.fb.control(''));
        this.form.addControl('vds', this.fb.control(''));
        this.form.addControl(
          'specialType',
          this.fb.control('', Validators.required)
        );
        this.form.addControl(
          'materialType',
          this.fb.control('', Validators.required)
        );
        this.form.addControl('subtypeSeries', this.fb.control(''));
        this.form.addControl('seriesCost', this.fb.control(''));
        this.form.addControl('emission', this.fb.control(''));
        this.form.addControl('destination', this.fb.control(''));
        this.form.addControl(
          'email',
          this.fb.control('', [
            Validators.pattern(
              '[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+[.]+[a-zA-Z]{2,4}$'
            )
          ])
        );
        this.form.addControl(
          'phone',
          this.fb.control('', [
            Validators.minLength(9),
            Validators.maxLength(9),
            Validators.pattern('^[0-9]*$')
          ])
        );
        break;
    }

    this.form.get('date')?.valueChanges.subscribe(() => {
      this.form.get('date')!.value < this.minDate;
    });

    this.form
      .get('dealerCode')
      ?.valueChanges.subscribe((value: string | Dealers) => {
        const filterValue = (
          typeof value === 'string' ? value : value?.name || value?.code || ''
        ).toLowerCase();

        if (!this.dealers) {
          this.filteredDealers = [];
          return;
        }

        this.filteredDealers = this.dealers.filter((dealer) => {
          const dealerName = dealer.name.toLowerCase();
          const dealerCode = dealer.code.toLowerCase();
          return (
            dealerName.includes(filterValue) || dealerCode.includes(filterValue)
          );
        });
      });

    this.form.valueChanges.subscribe(() => {
      const formValue = this.form.getRawValue();
      this.formChange.emit({ ...formValue, invalid: this.form.invalid });
    });
  }

  minDateToday(): ValidatorFn {
    //Validador para controlar que la fecha de necesidad no sea menor a hoy
    return (control: AbstractControl): { [key: string]: any } | null => {
      const inputDate = new Date(control.value);
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      if (inputDate < today) {
        return { minDateToday: { value: control.value } };
      }

      return null;
    };
  }

  minDateNeed(needDate: string): ValidatorFn {
    //Validador para controlar que la fecha de devolución no sea menor a la de necesidad
    return (control: AbstractControl): { [key: string]: any } | null => {
      const inputDateStr = control.value;

      if (!inputDateStr || !needDate) {
        return null;
      }

      const inputDate = new Date(inputDateStr);
      const otherDate = new Date(needDate);

      if (inputDate < otherDate) {
        return {
          minDateNeed: {
            value: control.value
          }
        };
      }

      return null;
    };
  }

  areaChange() {
    const areaSelected = this.form.get('area')?.value;

    this.orderService.getProjectDepartaments(areaSelected).subscribe({
      next: (data: any) => {
        this.departaments = data;
      },
      error: (error) => {
        console.error('Error fetching authorizers:', error);
      },
      complete: () => {
        console.log('Fetch complete');
      }
    });

    if (!areaSelected) {
      this.form.get('departament')?.disable();
    } else {
      this.form.get('departament')?.enable();
    }

    const formValue = this.form.getRawValue();
    this.formChange.emit({ ...formValue, invalid: this.form.invalid });
  }

  depChange() {
    const formValue = this.form.getRawValue();
    this.formChange.emit({ ...formValue, invalid: this.form.invalid });
  }

  updateDealerCode() {
    const selectedDealer: Dealers = this.form.value.dealerCode;

    if (selectedDealer && selectedDealer.preseries === false) {
      this.dialog.open(FeedbackModalComponent, {
        width: '530px',
        data: {
          text: i18next.t('nonPreseriesDealer'),
          close: true,
          theme: 'light'
        }
      });
    }
  }

  private applyDealer(dealer: Dealers): void {
    // Cargar cecos del dealer
    this.cecos = dealer.cecos ? [...dealer.cecos] : [];

    const cecoControl = this.form.get('ceco');
    if (!cecoControl) {
      return;
    }

    this.form.get('launch')?.patchValue(dealer.launch);

    // Habilitar control ceco
    cecoControl.enable({ emitEvent: false });

    // Si sólo hay un ceco, asignarlo automáticamente
    if (this.cecos.length === 1) {
      cecoControl.setValue(this.cecos[0], { emitEvent: false });
    } else {
      // Si hay varios, limpiar selección previa (pero no deshabilitar)
      cecoControl.setValue('', { emitEvent: false });
    }
  }

  private clearCecos(): void {
    this.cecos = [];
    const cecoControl = this.form.get('ceco');
    if (!cecoControl) {
      return;
    }
    cecoControl.setValue('', { emitEvent: false });
    cecoControl.disable({ emitEvent: false });
  }

  updateDealerCodeAC(event: any) {
    const selectedDealer: Dealers = event.option.value;

    if (selectedDealer) {
      this.applyDealer(selectedDealer);
    } else {
      this.clearCecos();
    }

    this.updateDealerCode();
  }

  close() {
    this.picker['_destroyOverlay']();
    this.datePicker['_destroyOverlay']();
    this.plateNumberPicker['_destroyOverlay']();
    this.deliveryPicker['_destroyOverlay']();
  }

  closeSop() {
    this.sopPicker['_destroyOverlay']();
  }

  calculateCosts() {
    if (this.form.get('preseriesType')?.value === 'EMPTY') {
      this.dialog.open(FeedbackModalComponent, {
        data: {
          text: i18next.t('errorNA'),
          close: true,
          theme: 'ligth'
        }
      });
    } else if (this.form.get('tmaimg')?.value === '') {
      this.dialog.open(FeedbackModalComponent, {
        data: {
          text: i18next.t('errorTmaimg'),
          close: true,
          theme: 'ligth'
        }
      });
    } else if (this.form.get('materialType')?.value === '') {
      this.dialog.open(FeedbackModalComponent, {
        data: {
          text: i18next.t('errorMaterialType'),
          close: true,
          theme: 'ligth'
        }
      });
      // } else if (this.form.get('preseriesType')?.value === '') {
      //   this.dialog.open(FeedbackModalComponent, {
      //     data: {
      //       text: i18next.t('errorPreseriesType'),
      //       close: true
      //     }
      //   });
    } else if (this.form.get('projectType')?.value === 'EMPTY') {
      this.dialog.open(FeedbackModalComponent, {
        data: {
          text: i18next.t('errorProjectType'),
          close: true
        }
      });
    } else {
      this.orderService
        .getCalculateCosts(
          this.form.get('tmaimg')?.value,
          this.form.get('materialType')?.value,
          this.form.get('preseriesType')?.value,
          this.form.get('projectType')?.value
        )
        .subscribe({
          next: (data: any) => {
            this.form.get('preseriesCost')?.setValue(data.preserieCost);
            this.form.get('seriesCost')?.setValue(data.serieCost);
            const formValue = this.form.getRawValue();
            this.formChange.emit({ ...formValue, invalid: this.form.invalid });
          },
          error: () => {
            this.dialog.open(FeedbackModalComponent, {
              data: {
                text: i18next.t('errorGetCosts'),
                close: true,
                theme: 'ligth'
              }
            });
          },
          complete: () => {
            console.log('Fetch complete');
          }
        });
    }
  }

  onSearch() {
    const vin = this.searchForm.get('vin')?.value;
    const plateNumber = this.searchForm.get('plateNumber')?.value;

    if (vin || plateNumber) {
      this.vehicleService.getVehiclesProjects(vin, plateNumber).subscribe(
        (vehicle) => {
          this.searchError = false;
          const existingVehicle = this.vehicles.find(
            (v) => v.vehicle.vin === vehicle.vin
          );
          if (existingVehicle) {
            this.vinRepeat = true;
            this.form.get('stockVehicle')?.setValue(false);
            this.stockVehicle = false;
          } else {
            this.vehicles.push(vehicle);
            this.vinRepeat = false;
            this.searchForm.reset();
            this.searchForm.get('vin')?.disable();
            this.searchForm.get('plateNumber')?.disable();
            this.form.get('vin')?.setValue(vehicle.vin);
            this.form.get('model')?.setValue(vehicle.model);
            this.form.get('color')?.clearValidators();
            this.form.get('color')?.updateValueAndValidity();
            this.form.get('finish')?.clearValidators();
            this.form.get('finish')?.updateValueAndValidity();
            this.form.get('tmaimg')?.clearValidators();
            this.form.get('tmaimg')?.updateValueAndValidity();
            this.form.get('stockVehicle')?.setValue(true);
            this.stockVehicle = true;
          }
        },
        (error) => {
          console.error('Error fetching vehicles:', error);
          this.searchError = true;
          this.form.get('stockVehicle')?.setValue(false);
          this.stockVehicle = false;
        }
      );
    }
  }

  removeVehicle(index: number) {
    this.vehicles.splice(index, 1);
    this.stockVehicle = false;
    this.searchForm.get('vin')?.enable();
    this.searchForm.get('plateNumber')?.enable();
    this.form.get('color')?.setValidators(Validators.required);
    this.form.get('color')?.updateValueAndValidity();
    this.form.get('finish')?.setValidators(Validators.required);
    this.form.get('finish')?.updateValueAndValidity();
    this.form.get('tmaimg')?.setValidators(Validators.required);
    this.form.get('tmaimg')?.updateValueAndValidity();
  }
}
